import type { MarketplaceListingLabel } from '__generated__/zeus/types';
import type { StatusTagVariant } from 'components/marketplace/map/refresh/StatusTag';

/**
 * Status that a listing label shows as: a StatusTag kind, or recently sold,
 * which has no status tag.
 */
export type ListingStatusKind = Exclude<StatusTagVariant, 'trending'> | 'recentlySold';

/**
 * Display priority of listing statuses, highest first. A surface with one
 * status slot shows the first status that a home has. A home under contract,
 * coming soon or sold is not on the market, so these lifecycle statuses come
 * before the on-market statuses.
 */
const LISTING_STATUS_PRIORITY = [
  'pendingSale',
  'comingSoon',
  'recentlySold',
  'newlyListed',
  'priceDrop',
] as const satisfies readonly ListingStatusKind[];

/** Statuses that tell where a home is in its sale, not how it is on the market. */
export const LIFECYCLE_LISTING_STATUSES = [
  'pendingSale',
  'comingSoon',
  'recentlySold',
] as const satisfies readonly ListingStatusKind[];

export type LifecycleListingStatus = (typeof LIFECYCLE_LISTING_STATUSES)[number];

const LABEL_STATUS = {
  COMING_SOON: 'comingSoon',
  NEWLY_LISTED: 'newlyListed',
  PENDING_SALE: 'pendingSale',
  PRICE_DROP: 'priceDrop',
  RECENTLY_SOLD: 'recentlySold',
  SELF_TOUR: null,
} as const satisfies Record<MarketplaceListingLabel, ListingStatusKind | null>;

/** The status a label shows as; null for a label that is not a status, such as self-tour. */
export const listingStatusKind = (label: MarketplaceListingLabel): ListingStatusKind | null =>
  LABEL_STATUS[label];

/**
 * The highest-priority status of a home among `kinds`, the statuses that a
 * surface can show. Null when the home has none of them.
 */
export const primaryListingStatus = <Kind extends ListingStatusKind>(
  labels: readonly MarketplaceListingLabel[] | null | undefined,
  kinds: readonly Kind[],
): Kind | null => {
  const statuses = new Set(labels?.map(listingStatusKind));
  const shown = new Set<ListingStatusKind>(kinds);
  return (
    LISTING_STATUS_PRIORITY.find(
      (status): status is Kind => shown.has(status) && statuses.has(status),
    ) ?? null
  );
};

/** Whether a home's labels include one that shows as `status`. */
export const hasListingStatus = (
  labels: readonly MarketplaceListingLabel[] | null | undefined,
  status: ListingStatusKind,
): boolean => labels?.some((label) => listingStatusKind(label) === status) ?? false;
